// Modal for editing the LLM prompts inline, right next to the actions that
// consume them. Lives on ReleaseDetail — the "Edit prompts" button opens
// this, editor tweaks the release_narrative + release_items templates,
// then hits Save and closes to try Regenerate.
//
// Replaces the dedicated /prompts page — the prior discovery cost (nav →
// Prompts → back to Releases → Regenerate) was too high for an iterative
// prompt-tuning loop.
import { useEffect, useState } from "react";
import type { PromptRow } from "@release-hub/shared";
import { useSavePrompt } from "@/api/mutations";
import { usePrompts } from "@/api/queries";
import { ApiError } from "@/api/client";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { useConfirm } from "@/ui/confirm";
import { fmtDateTime } from "@/lib/format";

const TITLES: Record<string, string> = {
  release_narrative: "Release narrative",
  release_items: "Release items extraction",
};

export function PromptsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: prompts, isPending, error } = usePrompts();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Override the default max-width — prompts have 500+ char lines and
          need real estate. Also cap height + scroll so long templates
          don't overflow off-screen. */}
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit LLM prompts</DialogTitle>
          <DialogDescription>
            Templates driving Convert &amp; Regenerate. Variables like{" "}
            <code className="rounded bg-border/40 px-1 font-mono text-xs">
              {"{{body}}"}
            </code>{" "}
            are substituted at call time. Save, close, then hit Regenerate.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
          {isPending && (
            <p className="text-text-secondary">Loading prompts…</p>
          )}
          {error && (
            <p className="text-destructive">
              Failed to load:{" "}
              {error instanceof Error ? error.message : "unknown"}
            </p>
          )}
          {prompts?.map((p) => <PromptCard key={p.key} prompt={p} />)}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromptCard({ prompt }: { prompt: PromptRow }) {
  const save = useSavePrompt();
  const confirm = useConfirm();
  const [draft, setDraft] = useState(prompt.content);
  const [err, setErr] = useState<string>("");
  const [historyOpen, setHistoryOpen] = useState(false);

  // Keep the textarea in sync with fresh server data (e.g. after a reset).
  useEffect(() => {
    setDraft(prompt.content);
  }, [prompt.content]);

  const dirty = draft !== prompt.content;
  const title = TITLES[prompt.key] ?? prompt.key;

  const submit = async (content: string | null) => {
    setErr("");
    try {
      await save.mutateAsync({ key: prompt.key, content });
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.detail
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  };

  return (
    <section className="rounded-md border border-border bg-bg/40 p-4">
      <header className="mb-2 flex items-center gap-3">
        <h3 className="font-serif text-base">{title}</h3>
        <span
          className={
            prompt.is_default
              ? "rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary"
              : "rounded-full bg-ga-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-ga"
          }
        >
          {prompt.is_default ? "Default" : "Custom"}
        </span>
      </header>
      {prompt.updated_at && (
        <p className="mb-2 text-xs text-text-tertiary">
          Last edited {fmtDateTime(prompt.updated_at)}
          {prompt.updated_by ? ` by ${prompt.updated_by}` : ""} ·{" "}
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="underline decoration-dotted hover:text-text"
          >
            View history
          </button>
        </p>
      )}
      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType="llm_prompts"
        entityId={prompt.key}
        title={title}
      />
      <textarea
        className="min-h-[240px] w-full rounded-md border border-border-hover bg-card p-3 font-mono text-[12.5px] leading-relaxed focus:border-accent focus:outline-none disabled:opacity-60"
        value={draft}
        disabled={save.isPending}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="mt-2 flex items-center justify-end gap-2">
        {err && <span className="text-xs text-destructive">{err}</span>}
        <Button
          size="sm"
          variant="outline"
          disabled={prompt.is_default || save.isPending}
          onClick={async () => {
            const ok = await confirm({
              title: "Reset this prompt to the built-in default?",
              description:
                "Your custom prompt will be discarded. The LLM will use the module-level default until you save a new one.",
              confirmLabel: "Reset",
            });
            if (ok) submit(null);
          }}
        >
          Reset to default
        </Button>
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => submit(draft)}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </section>
  );
}
